// Tools tab — lists tools registered by Evisio2SdkWiring against the mock connection,
// shows their schema, lets the dev fill a JSON params textarea and Run the tool. The
// run path calls tool.handler(args) directly — same call shape the real SDK makes —
// so the bridge round-trip into the eGo iframe is exercised end-to-end.

import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './simulatorToolsTab.html?raw';
import lang from 'dojo/_base/lang';
import _WidgetBaseSP from 'more/_WidgetBaseSP';

import type { Tool } from "../../../sdk/evisio2sdk";
import { MockEvisio2Sdk } from "../../mockSdk/mockEvisio2Sdk";
import { MockEvisio2Connection, MockStateChannel } from "../../mockSdk/mockEvisio2Connection";

interface HistoryEntry {
    when: string;
    tool: string;
    args: any;
    result: any;
    durationMs: number;
}

class simulatorToolsTab extends compose(_WidgetBaseSP) {
    templateString: any = template;

    public sdk: MockEvisio2Sdk;
    public connection: MockEvisio2Connection;

    private toolPicker:         HTMLSelectElement;
    private selectedToolHeader: HTMLDivElement;
    private selectedToolDesc:   HTMLDivElement;
    private selectedToolSchema: HTMLPreElement;
    private schemaDetails:      HTMLDetailsElement;
    private payloadInput:       HTMLTextAreaElement;
    private runBtn:             HTMLButtonElement;
    private runStatus:          HTMLDivElement;
    private resultDisplay:      HTMLPreElement;
    private historyContainer:   HTMLDivElement;

    private _selectedToolName: string | null = null;
    private _history: HistoryEntry[] = [];

    private _stateChangeDisposer: (() => void) | null = null;

    constructor(args?: { sdk: MockEvisio2Sdk; connection: MockEvisio2Connection }){
        super(...(arguments as unknown as []));
        if (args){ this.sdk = args.sdk; this.connection = args.connection; }
    }

    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        this.runBtn.addEventListener("click", lang.hitch(this, this._onClickRun));
        this.toolPicker.addEventListener("change", lang.hitch(this, this._onToolPickerChange));
        this._renderToolPicker();

        this._stateChangeDisposer = this.connection.addStateChangeListener(lang.hitch(this, this._onStateChange));
    }

    startup(): void { super.startup.apply(this, arguments as any); }

    destroy(): void {
        if (this._stateChangeDisposer){ try { this._stateChangeDisposer(); } catch { /* ignore */ } this._stateChangeDisposer = null; }
        super.destroy.apply(this, arguments as any);
    }

    private _onStateChange(channel: MockStateChannel): void {
        if (channel === "tools") this._renderToolPicker();
    }

    private _renderToolPicker(): void {
        let tools = this.connection._getRegisteredTools();
        let prevSelection = this._selectedToolName;
        this.toolPicker.innerHTML = "";

        let placeholder = document.createElement("option");
        placeholder.value = "";
        placeholder.textContent = tools.length === 0 ? "(no tools registered)" : "— pick one —";
        this.toolPicker.appendChild(placeholder);

        for (let i = 0; i < tools.length; i++){
            let t = tools[i];
            let opt = document.createElement("option");
            opt.value = t.name;
            opt.textContent = (t.displayName || t.name) + "  (" + t.name + ")";
            this.toolPicker.appendChild(opt);
        }

        // Preserve selection if still valid.
        if (prevSelection && this.connection._getRegisteredTool(prevSelection)){
            this.toolPicker.value = prevSelection;
        } else {
            this._selectedToolName = null;
            this.toolPicker.value = "";
        }
        this._renderSelectedPane();
    }

    private _onToolPickerChange(): void {
        let name = this.toolPicker.value || null;
        this._selectedToolName = name;
        // Clear the auto-filled marker so the seed payload regenerates for the new tool.
        this.payloadInput.removeAttribute("data-auto-filled");
        this.payloadInput.value = "";
        this._renderSelectedPane();
    }

    private _renderSelectedPane(): void {
        if (!this._selectedToolName){
            this.selectedToolHeader.textContent = "Pick a tool from the dropdown.";
            this.selectedToolDesc.textContent = "";
            this.selectedToolSchema.textContent = "";
            this.payloadInput.value = "";
            return;
        }
        let tool = this.connection._getRegisteredTool(this._selectedToolName);
        if (!tool){
            this.selectedToolHeader.textContent = "Tool no longer registered";
            return;
        }
        this.selectedToolHeader.textContent = tool.displayName || tool.name;
        let nameLine = "name: " + tool.name;
        this.selectedToolDesc.textContent = nameLine + "\n\n" + (tool.description || "(no description)");
        let schemaText = "";
        try { schemaText = JSON.stringify(tool.schema, null, 2); } catch { schemaText = String(tool.schema); }
        this.selectedToolSchema.textContent = schemaText || "(no schema)";

        // Pre-populate payload textarea with an empty object that matches the param shape.
        if (this.payloadInput.value === "" || this.payloadInput.getAttribute("data-auto-filled") === "1"){
            this.payloadInput.value = this._buildSeedPayload(tool);
            this.payloadInput.setAttribute("data-auto-filled", "1");
        }
    }

    private _buildSeedPayload(tool: Tool): string {
        let props: any = tool.schema && tool.schema.parameters && tool.schema.parameters.properties;
        if (!props || typeof props !== "object") return "{}";
        let seed: any = {};
        let keys = Object.keys(props);
        for (let i = 0; i < keys.length; i++){
            let k = keys[i];
            let p = props[k];
            switch (p && p.type){
                case "string":  seed[k] = ""; break;
                case "number":  seed[k] = 0;  break;
                case "boolean": seed[k] = false; break;
                case "array":   seed[k] = []; break;
                case "object":  seed[k] = {}; break;
                default:        seed[k] = null;
            }
        }
        return JSON.stringify(seed, null, 2);
    }

    private async _onClickRun(): Promise<void> {
        if (!this._selectedToolName){ this._setRunStatus("Pick a tool first", true); return; }
        let tool = this.connection._getRegisteredTool(this._selectedToolName);
        if (!tool){ this._setRunStatus("Tool no longer registered", true); return; }

        let args: any;
        try { args = JSON.parse(this.payloadInput.value || "{}"); }
        catch (e){ this._setRunStatus("Invalid JSON: " + (e as any).message, true); return; }

        this._setRunStatus("running…", false);
        this.runBtn.disabled = true;
        let started = performance.now();
        let result: any;
        try {
            result = await Promise.resolve(tool.handler(args));
            let duration = Math.round(performance.now() - started);
            this._renderResult(result);
            this._setRunStatus("done in " + duration + "ms", false);
            this._pushHistory({ when: this._nowStamp(), tool: tool.name, args: args, result: result, durationMs: duration });
        } catch (e: any){
            let duration = Math.round(performance.now() - started);
            let errVal = { caught: true, kind: e && e.kind, message: e && e.message ? e.message : String(e) };
            this._renderResult(errVal);
            this._setRunStatus("threw in " + duration + "ms", true);
            this._pushHistory({ when: this._nowStamp(), tool: tool.name, args: args, result: errVal, durationMs: duration });
        } finally {
            this.runBtn.disabled = false;
        }
    }

    private _renderResult(val: any): void {
        let txt = "";
        try { txt = JSON.stringify(val, null, 2); } catch { txt = String(val); }
        this.resultDisplay.textContent = txt;
    }

    private _setRunStatus(msg: string, isError: boolean): void {
        this.runStatus.textContent = msg;
        this.runStatus.style.color = isError ? "#ff8c8c" : "#9bd07b";
    }

    private _pushHistory(entry: HistoryEntry): void {
        this._history.unshift(entry);
        if (this._history.length > 10) this._history.length = 10;
        this._renderHistory();
    }

    private _renderHistory(): void {
        this.historyContainer.innerHTML = "";
        for (let i = 0; i < this._history.length; i++){
            let h = this._history[i];
            let row = document.createElement("div");
            row.style.cssText = "border:1px solid #2a2b30; border-radius:3px; padding:4px 6px; background:#16171a; font-family:Consolas,monospace;";
            let resultSnippet: string;
            try { resultSnippet = JSON.stringify(h.result); } catch { resultSnippet = String(h.result); }
            if (resultSnippet && resultSnippet.length > 120) resultSnippet = resultSnippet.substring(0, 117) + "...";
            row.innerHTML =
                "<div style=\"display:flex; gap:6px; align-items:baseline;\">" +
                    "<span style=\"opacity:0.55;\">" + this._escapeHtml(h.when) + "</span>" +
                    "<span style=\"font-weight:600;\">" + this._escapeHtml(h.tool) + "</span>" +
                    "<span style=\"opacity:0.55;\">(" + h.durationMs + "ms)</span>" +
                "</div>" +
                "<div style=\"opacity:0.8; font-size:11px; margin-top:2px;\">" + this._escapeHtml(resultSnippet) + "</div>";
            this.historyContainer.appendChild(row);
        }
    }

    private _nowStamp(): string {
        let d = new Date();
        let pad = (n: number) => String(n).padStart(2, "0");
        return pad(d.getHours()) + ":" + pad(d.getMinutes()) + ":" + pad(d.getSeconds());
    }

    private _escapeHtml(s: string): string {
        if (s == null) return "";
        return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    }
}

export default mdsWidget(simulatorToolsTab);
